import { Badge } from '@/components/ui/Badge'
import { InfoTooltip } from '@/components/ui/Menu'
import {
  inspectionStatusMeta,
  issueStatusMeta,
  priorityMeta,
  templateStatusMeta,
  type StatusMeta,
} from '@/lib/status'

function render(meta: StatusMeta, size: 'sm' | 'md', withTooltip: boolean) {
  const badge = (
    <Badge tone={meta.tone} size={size} icon={<meta.icon className="size-3" aria-hidden />}>
      {meta.label}
    </Badge>
  )
  // Colour + icon + text; the tooltip adds the "what does this mean" layer.
  if (!withTooltip || !meta.description) return badge
  return (
    <InfoTooltip label={meta.description}>
      <span className="cursor-help">{badge}</span>
    </InfoTooltip>
  )
}

export function StatusBadge({
  status,
  size = 'md',
  withTooltip = true,
}: {
  status: string
  size?: 'sm' | 'md'
  withTooltip?: boolean
}) {
  return render(inspectionStatusMeta(status), size, withTooltip)
}

export function PriorityBadge({
  priority,
  size = 'md',
  withTooltip = true,
}: {
  priority: string
  size?: 'sm' | 'md'
  withTooltip?: boolean
}) {
  return render(priorityMeta(priority), size, withTooltip)
}

export function IssueStatusBadge({
  status,
  size = 'md',
  withTooltip = true,
}: {
  status: string
  size?: 'sm' | 'md'
  withTooltip?: boolean
}) {
  return render(issueStatusMeta(status), size, withTooltip)
}

export function TemplateStatusBadge({
  status,
  size = 'md',
  withTooltip = true,
}: {
  status: string
  size?: 'sm' | 'md'
  withTooltip?: boolean
}) {
  return render(templateStatusMeta(status), size, withTooltip)
}

/** Completion bar. The number is present as text, not encoded in width alone. */
export function ProgressBar({
  value,
  total,
  percent,
  className,
}: {
  value: number
  total: number
  percent: number
  className?: string
}) {
  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <div
          className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-surface-sunken"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${value} of ${total} checks complete`}
        >
          <div
            className={
              percent >= 100 ? 'h-full bg-success' : percent > 0 ? 'h-full bg-accent' : 'h-full'
            }
            style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
          />
        </div>
        <span className="tabular text-xs text-fg-muted">{percent}%</span>
      </div>
    </div>
  )
}
